import type { LayoutOverride } from '../types'

/**
 * Converte número para px, mantém string como está
 */
export function applyCoordinate(value: string | number | undefined): string | undefined {
  if (value === undefined) return undefined
  return typeof value === 'number' ? `${value}px` : value
}

export function offsetTop(
  baseTop: string | number | undefined,
  offset?: string | number
): string | number | undefined {
  if (baseTop === undefined || offset === undefined) return baseTop

  const baseTopStr = typeof baseTop === 'number' ? `${baseTop}px` : baseTop

  if (typeof offset === 'string') {
    const trimmed = offset.trim()
    if (!trimmed) return baseTopStr

    if (trimmed.endsWith('%')) {
      const isNegative = trimmed.startsWith('-')
      const absValue = isNegative ? trimmed.slice(1) : trimmed
      const operator = isNegative ? '-' : '+'
      return `calc(${baseTopStr} ${operator} ${absValue})`
    }

    const numeric = parseFloat(trimmed)
    if (Number.isNaN(numeric)) return baseTopStr

    const operator = numeric < 0 ? '-' : '+'
    const absValue = Math.abs(numeric)
    const hasPxSuffix = trimmed.toLowerCase().includes('px')
    const unit = hasPxSuffix ? 'px' : 'px'
    return `calc(${baseTopStr} ${operator} ${absValue}${unit})`
  }

  const operator = offset < 0 ? '-' : '+'
  const absValue = Math.abs(offset)
  return `calc(${baseTopStr} ${operator} ${absValue}px)`
}

/**
 * Mescla estilo base com overrides do passo.
 * Suporta: top, left, right, bottom, width, maxWidth, zIndex,
 * scale, mirror, textAlign, justifyContent, gap, marginTop
 */
export function mergeStyles(
  baseStyle: React.CSSProperties,
  override?: Record<string, any>
): React.CSSProperties {
  if (!override) return baseStyle
  
  const merged = { ...baseStyle }
  
  // Coordenadas
  if (override.top !== undefined) merged.top = applyCoordinate(override.top)
  if (override.left !== undefined) merged.left = applyCoordinate(override.left)
  if (override.right !== undefined) merged.right = applyCoordinate(override.right)
  if (override.bottom !== undefined) merged.bottom = applyCoordinate(override.bottom)
  
  // Dimensões (width/height sobrescrevem valores base)
  if (override.width !== undefined) merged.width = applyCoordinate(override.width)
  if (override.height !== undefined) merged.height = applyCoordinate(override.height)
  if (override.maxWidth !== undefined) merged.maxWidth = applyCoordinate(override.maxWidth)
  
  if (override.zIndex !== undefined) merged.zIndex = override.zIndex
  
  // Transform: translate, scale e mirror
  // Aplicamos diretamente no transform para funcionar com e sem animações
  const transforms: string[] = []

  // Preservar transform existente (se houver)
  const existingTransform = (merged.transform as string) || ''
  if (existingTransform && existingTransform.trim()) {
    transforms.push(existingTransform.trim())
  }

  if (override.translateX !== undefined && override.translateY !== undefined) {
    const translateX = applyCoordinate(override.translateX)
    const translateY = applyCoordinate(override.translateY)
    transforms.push(`translate(${translateX}, ${translateY})`)
  } else {
    if (override.translateX !== undefined) {
      transforms.push(`translateX(${applyCoordinate(override.translateX)})`)
    }
    if (override.translateY !== undefined) {
      transforms.push(`translateY(${applyCoordinate(override.translateY)})`)
    }
  }

  if (override.scale !== undefined) {
    transforms.push(`scale(${override.scale})`);
    // Também adicionar CSS Variable para animações que usam
    (merged as any)['--character-scale'] = override.scale
  }

  if (override.mirror === true) {
    transforms.push('scaleX(-1)')
  }

  if (transforms.length > 0) {
    merged.transform = transforms.join(' ')
  }
  
  // Alinhamento de texto
  if (override.textAlign !== undefined) merged.textAlign = override.textAlign
  
  // Flexbox
  if (override.justifyContent !== undefined) merged.justifyContent = override.justifyContent
  if (override.gap !== undefined) merged.gap = applyCoordinate(override.gap)
  if (override.marginTop !== undefined) merged.marginTop = applyCoordinate(override.marginTop)
  
  return merged
}

/**
 * Calcula estilo do tail baseado no placement e offsets
 */
export function getTailStyle(
  baseTailStyle: React.CSSProperties,
  tailOverride?: LayoutOverride['tail']
): React.CSSProperties {
  if (!tailOverride) return baseTailStyle

  if (tailOverride.placement === 'none') {
    return { ...baseTailStyle, display: 'none' }
  }

  const style: React.CSSProperties = { ...baseTailStyle }

  const resetBorders = () => {
    style.borderTop = '8px solid transparent'
    style.borderBottom = '8px solid transparent'
    style.borderLeft = '8px solid transparent'
    style.borderRight = '8px solid transparent'
  }

  switch (tailOverride.placement) {
    case 'top': {
      resetBorders()
      delete style.right
      delete style.bottom
      style.top = -16
      style.left = '50%'
      style.transform = 'translateX(-50%)'
      style.borderBottom = '8px solid #8B5CF6'
      break
    }
    case 'bottom': {
      resetBorders()
      delete style.right
      delete style.top
      style.bottom = -8
      style.left = '50%'
      style.transform = 'translateX(-50%)'
      style.borderTop = '8px solid #8B5CF6'
      break
    }
    case 'left': {
      resetBorders()
      delete style.right
      delete style.bottom
      style.left = -8
      style.top = '50%'
      style.transform = 'translateY(-50%)'
      style.borderRight = '8px solid #8B5CF6'
      break
    }
    case 'right': {
      resetBorders()
      delete style.left
      delete style.bottom
      style.right = -8
      style.top = '50%'
      style.transform = 'translateY(-50%)'
      style.borderLeft = '8px solid #8B5CF6'
      break
    }
    default: {
      // Manter estilo base caso placement não seja especificada
      break
    }
  }

  // Aplicar offsets
  if (tailOverride.offsetX !== undefined) {
    if (style.left !== undefined) {
      style.left = typeof style.left === 'number'
        ? style.left + tailOverride.offsetX
        : `calc(${style.left} + ${tailOverride.offsetX}px)`
    }
    if (style.right !== undefined) {
      style.right = typeof style.right === 'number'
        ? style.right + tailOverride.offsetX
        : `calc(${style.right} + ${tailOverride.offsetX}px)`
    }
  }

  if (tailOverride.offsetY !== undefined) {
    if (style.top !== undefined) {
      style.top = typeof style.top === 'number'
        ? style.top + tailOverride.offsetY
        : `calc(${style.top} + ${tailOverride.offsetY}px)`
    }
    if (style.bottom !== undefined) {
      style.bottom = typeof style.bottom === 'number'
        ? style.bottom + tailOverride.offsetY
        : `calc(${style.bottom} + ${tailOverride.offsetY}px)`
    }
  }

  return style
}
